// Angular imports
import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

// Rxjs imports
import { Subscription } from 'rxjs';

// CC-Common imports
import { CCLocalStorageService, CCLocalStorageCategory, Utils } from 'cc-common';

// Component imports
import { BaseComponent } from '../../_base/mp-base-component';
import { SideNavigationMenuComponent } from '../side-navigation-menu/side-navigation-menu.component';

// Service imports
import { AuthService } from '../../_services/auth.service';
import { UserService } from '../../_services/data-services/user.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { NavigationService } from '../../_services/shared-services/navigation.service';
import { ContextManagerService } from '../../_services/data-services/context-manager.service';

// Model imports
import { SideNavTreeInfo } from '../../_models/side-nav-tree-info';
import { PropertyCommunityInfoResultDto } from '../../_models/property-community-info-result-dto';
import { ActionAccessListParameterDto } from '../../_models/action-access-list-parameter-dto';
import { ActionAccessResultDto } from '../../_models/action-access-result-dto';

// Constant imports
import { Constants } from '../../_constants/constants';
import { StorageKeyConstants } from '../../_constants/storage-key-constants';
import { SideNavigationConstants } from '../../_constants/navigation-constants';
import { RouteDataConstants } from '../../_constants/route-data-constants';

@Component({
    selector: 'app-side-nav-outer-toolbar',
    templateUrl: './side-nav-outer-toolbar.component.html',
    styleUrls: ['./side-nav-outer-toolbar.component.scss']
})
export class SideNavOuterToolbarComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('sideNavigationTree', { static: false }) sideNavMenu: SideNavigationMenuComponent;

    @Input() title: string;

    topMenuItems: Array<SideNavTreeInfo> = [];
    bottomMenuItems: Array<SideNavTreeInfo> = [];
    selectedRoute = null;
    menuOpened: boolean;
    temporaryMenuOpened = false;
    temporaryMenuClosed = false;
    menuMode = 'shrink';
    menuRevealMode = 'expand';
    minMenuSize = 0;
    shaderEnabled = false;
    enableSideMenu = false;
    helpLink: string;
    autoShowTopMenu = true;

    selectedContext: PropertyCommunityInfoResultDto;
    itemToSelect = null;

    drawerTimer: any;
    submenuItems: { parentId: string; items: SideNavTreeInfo[] }[] = [];
    contextSubscription: Subscription;
    navigationSubscription: Subscription;

    showTopNavigationMenu = true;

    constructor(
        protected readonly screenService: ScreenService,
        private readonly router: Router,
        private readonly authService: AuthService,
        private readonly userService: UserService,
        private readonly helperService: HelperService,
        private readonly navigationService: NavigationService,
        private readonly contextManagerService: ContextManagerService,
        private readonly localStorageService: CCLocalStorageService
    ) {
        super(screenService);
        this.router.events.subscribe((val) => {
            if (val instanceof NavigationEnd) {
                this.selectedRoute = val.urlAfterRedirects.split('?')[0];
                if (Utils.equals(this.selectedRoute, '/home')) {
                    /* NOTE:
                        -- navigating from community/property context to portfolio context.
                        -- selectedContext is set to null when context is reset, but url change happens after route navigation only. Hence this fix is required.
                    */
                    this.prepareSideNavigationItems();
                }
            }
        });
    }

    ngOnInit() {
        this.enableSideMenu = this.loggedIn() === true;
        this.menuOpened = this.screenService.isLargeScreen();
        this.onScreenSizeChanged();

        // user preferences
        this.autoShowTopMenu =
            (this.localStorageService.getItem(StorageKeyConstants.userTopMenuKey, CCLocalStorageCategory.PREF) ||
                'yes') === 'yes';
        this.userService.getUserPreferences().subscribe((preferences) => {
            if (preferences) {
                this.autoShowTopMenu =
                    (this.localStorageService.getItem(
                        StorageKeyConstants.userTopMenuKey,
                        CCLocalStorageCategory.PREF
                    ) || 'yes') === 'yes';
            }
        });

        this.helperService.subjectAutoSelectHomeMenuItem.subscribe((routeToSelect: string) => {
            this.selectedRoute = null;
            // Forcing selectedRoute change
            setTimeout(() => (this.selectedRoute = routeToSelect), 250);
        });

        this.userService.getSideNavigatorState().subscribe((mode: boolean) => {
            if ((this.menuOpened && !mode) || this.temporaryMenuClosed) {
                this.menuOpened = mode;
                this.temporaryMenuClosed = !this.temporaryMenuClosed;
                if (mode) {
                    this.updateOverlayCSSClass();
                }
            }
        });

        this.userService.getTopMenuState().subscribe((mode: boolean) => {
            this.showTopNavigationMenu = mode;
        });

        this.prepareSideNavigationItems();
        this.contextSubscription = this.contextManagerService.getContext().subscribe(() => {
            if (this.authService.isLoggedIn) {
                this.prepareSideNavigationItems();
            }
        });

        this.navigationSubscription = this.navigationService
            .onWorkspaceMenus()
            .subscribe((value: { parentId: string; items: SideNavTreeInfo[] }) => {
                const menuItem = this.navigationService.getSideNavItemById(this.topMenuItems, value.parentId);
                if (!!menuItem) {
                    menuItem.items = value.items;
                    if (!!this.sideNavMenu) {
                        this.sideNavMenu.refreshTopTree();
                    }
                } else {
                    this.submenuItems.push(value);
                }
            });
    }

    ngAfterViewInit(): void {
        // fix the issue that there is a big gap between side menu and page content in Safari.
        setTimeout(() => {
            const contentElms = document.getElementsByClassName(SideNavigationConstants.DRAWER_CLASS);
            if (contentElms.length > 0) {
                const item = contentElms[contentElms.length - 1] as HTMLElement;
                item.style.width = this.getMenuWidth();
                item.style.height = '100%';
                item.id = 'drawer-panel-content';
            }
        }, 10);
    }

    ngOnDestroy() {
        if (!!this.contextSubscription) {
            this.contextSubscription.unsubscribe();
        }
        if (!!this.navigationSubscription) {
            this.navigationSubscription.unsubscribe();
        }
    }

    get hideMenuAfterNavigation(): string | boolean {
        return this.menuMode === 'overlap' || this.temporaryMenuOpened;
    }

    get showMenuAfterClick(): boolean {
        return !this.menuOpened;
    }

    // hamburger menu click event handler
    processMenuToggle(): void {
        this.menuOpened = !this.menuOpened;
        // ! this one fixed the hamburger menu issue
        setTimeout(() => {
            const updateElement = (
                elm: HTMLElement,
                width: string,
                transform = SideNavigationConstants.RESET_TRANSLATE
            ) => {
                elm.style.width = width;
                elm.style.transform = transform;
            };

            let elms: HTMLCollectionOf<any>;
            if (this.screenService.isXSmallScreen()) {
                // ! in real iPhone device.
                elms = document.getElementsByClassName(SideNavigationConstants.OVERLAY_CLASS);
                if (elms.length > 0) {
                    // [DEV-4183] The 'OVERLAY_CLASS' appears in both the side-nav and popups elements.
                    // When the screen size is small or extra small and the side navigation toggles while a popup is open,
                    // Causing the popup's width to change to match the navbar's width. For that check has been added to handle this behavior.
                    const isPopupEle = elms[0].classList.toString().includes('popup');
                    if (Utils.isFalse(isPopupEle)) {
                        updateElement(elms[0] as HTMLElement, SideNavigationConstants.EXPANDED_WIDTH);
                    }
                }
                elms = document.getElementsByClassName(SideNavigationConstants.DRAWER_OVERLAY_CLASS);
                if (elms.length > 0) {
                    updateElement(
                        elms[0] as HTMLElement,
                        SideNavigationConstants.EXPANDED_WIDTH,
                        this.menuOpened
                            ? SideNavigationConstants.RESET_TRANSLATE
                            : SideNavigationConstants.SET_TRANSLATE
                    );
                }
            } else {
                // > xsmall devices:
                elms = document.getElementsByClassName(SideNavigationConstants.OVERLAY_CLASS);

                if (elms.length > 0) {
                    // [DEV-4183] The 'OVERLAY_CLASS' appears in both the side-nav and popups elements.
                    // When the screen size is small or extra small and the side navigation toggles while a popup is open,
                    // Causing the popup's width to change to match the navbar's width. For that check has been added to handle this behavior.
                    const isPopupEle = elms[0].classList.toString().includes('popup');
                    if (Utils.isFalse(isPopupEle)) {
                        updateElement(elms[0] as HTMLElement, this.getMenuWidth());
                    }
                }
                elms = document.getElementsByClassName(SideNavigationConstants.DRAWER_OVERLAY_CLASS);
                if (elms.length > 0) {
                    updateElement(elms[0] as HTMLElement, this.getMenuWidth());
                }

                if (this.menuOpened) {
                    elms = document.getElementsByClassName(SideNavigationConstants.DRAWER_CLASS);
                    if (elms.length > 0) {
                        updateElement(elms[0] as HTMLElement, this.getMenuWidth());
                    }
                }

                // [DEV-3990] Removed publish to subjectSideNavSizeChanged as it's not required.
                // SideNavigationMenuComponent handles ngOnChanges binded using [compactMode]="menuOpened" flag
            }
        }, 100);
    }

    onScreenSizeChanged(): void {
        super.onScreenSizeChanged();
        this.menuMode = this.isLarge ? 'shrink' : 'overlap';
        this.menuRevealMode = this.isXtraSmall ? 'slide' : 'expand';
        this.minMenuSize = this.isXtraSmall ? 0 : 60;
        this.shaderEnabled = !this.isLarge;
        if (this.menuOpened !== this.isLarge) {
            this.processMenuToggle();
        } else {
            this.menuOpened = this.isLarge;
        }
    }

    onNavigationItemClick(event) {
        const itemData = event.itemData;
        if (itemData.path) {
            this.navigationChanged(event);
        } else if (itemData.isWorkspace) {
            this.navigationService.updateWorkspaceSelection(itemData);
        }
    }

    onOpenMenuClick(): void {
        if (this.showMenuAfterClick) {
            this.temporaryMenuOpened = true;
            this.menuOpened = true;

            // fix ipad issue that the menu just shows half
            this.updateOverlayCSSClass();
        }
    }

    loggedIn(): boolean {
        return this.authService.isLoggedIn;
    }

    getMenuWidth(): string {
        // fix the issue where controls on the left hand side can't be touch because the side menu overlaps.
        const isLarge = this.screenService.sizes['screen-large'];
        const mode = isLarge || this.menuOpened === true ? 'shrink' : 'overlap'; // https://js.devexpress.com/Demos/WidgetsGallery/Demo/Drawer/LeftOrRightPosition/Angular/Light/
        return mode === 'shrink' ? '330px' : '60px';
    }

    private updateOverlayCSSClass(): void {
        setTimeout(() => {
            const overlayClass = SideNavigationConstants.DRAWER_CLASS;
            if (document.getElementsByClassName(overlayClass).length > 0) {
                const item: any = document.getElementsByClassName(overlayClass)[0];
                item.style.width = this.getMenuWidth();
                item.style.transform = SideNavigationConstants.RESET_TRANSLATE;
            }
        }, 150);
    }

    private getRouteFromChildren(parentRoute, routePath, rootPath): any {
        let route = null;
        const childRoutes = parentRoute.children || parentRoute._loadedConfig.routes;
        for (const childRoute of childRoutes) {
            const checkPath = `${rootPath}/${childRoute.path}`;
            const pathFound = routePath.toLowerCase() === checkPath.toLowerCase();
            if (pathFound) {
                route = childRoute;
            } else if (childRoute.children && childRoute.children.length > 0) {
                route = this.getRouteFromChildren(childRoute, routePath, checkPath);
            } else if (
                childRoute._loadedConfig &&
                childRoute._loadedConfig.routes &&
                childRoute._loadedConfig.routes.length > 0
            ) {
                route = this.getRouteFromChildren(childRoute, routePath, checkPath);
            }
            if (!!route) {
                break;
            }
        }
        return route;
    }

    private navigationChanged(event): any {
        const navigationItem = event.itemData;
        const path: string = event.itemData.path;
        const urlPath = this.getURLPath(path);
        const selectedRoute = this.getSafeURLPath(this.selectedRoute);
        const routePath = this.getSafeURLPath(path);

        if (urlPath === 'home' && !this.helperService.isOpenLinksExternal()) {
            if (this.selectedContext && (this.selectedContext.communityId || this.selectedContext.customerId)) {
                this.contextManagerService.updateContext(null); // no context
            }
            if (this.isSmall && this.menuOpened) {
                setTimeout(() => (this.menuOpened = !this.menuOpened), 0);
            }
        }

        if (urlPath && selectedRoute !== urlPath) {
            let route = RouteDataConstants.getRouteDataByPath(path);
            if (!route) {
                for (const routeConfig of this.router.config) {
                    const parentRoute: any = routeConfig as any;
                    if (parentRoute.path.toLowerCase() === routePath.toLowerCase()) {
                        route = parentRoute;
                    } else if (parentRoute.children && parentRoute.children.length > 0) {
                        route = this.getRouteFromChildren(parentRoute, routePath, parentRoute.path);
                    } else if (
                        parentRoute._loadedConfig &&
                        parentRoute._loadedConfig.routes &&
                        parentRoute._loadedConfig.routes.length > 0
                    ) {
                        route = this.getRouteFromChildren(parentRoute, routePath, parentRoute.path);
                    }
                    if (!!route) {
                        break;
                    }
                }
            }

            let newWindow: Window;
            const queryParams = event.itemData.params ? `?${this.getURLPath(event.itemData.params)}` : '';
            if (route && route.data && route.data.newWindow) {
                if (route.data.newWindow && !route.data.resizable) {
                    this.helperService.openWindow(`${urlPath}${queryParams}`, '_blank', null, null);
                } else {
                    let winWidth = screen.width;
                    let winHeight = screen.height;
                    if (route.data.resizable) {
                        winWidth = route.data.width || Constants.defaultPopupWindowWidth;
                        winHeight = route.data.height || Constants.defaultPopupWindowHeight;
                    }
                    const specs = `top=0,left=0,scrollbars=0,resizable=0,fullscreen=1,width=${winWidth},height=${winHeight}`;
                    newWindow = window.open(`${urlPath}${queryParams}`, '_blank', specs);
                    if (Utils.notNullAndDefined(newWindow)) {
                        newWindow.focus();
                    }
                }
            } else if (this.helperService.isOpenLinksExternal()) {
                this.helperService.openWindow(`${urlPath}${queryParams}`, '_blank', null, null);
            } else {
                if (Utils.isTrue(navigationItem.openInNewWindow)) {
                    this.helperService.openWindow(`${urlPath}${queryParams}`, '_blank', null, null);
                } else {
                    this.router.navigate([urlPath]);
                }
            }
            if (!!newWindow && window.location.pathname !== `/${urlPath}`) {
                setTimeout(() => this.sideNavMenu.selectPreviousSelectedItem(selectedRoute), 10);
            }
            this.contextManagerService.setSelectedContextRouteItem(path);
            if (this.hideMenuAfterNavigation) {
                this.temporaryMenuOpened = false;
                this.menuOpened = false;
                this.helperService.subjectSideNavSizeChanged.next(this.menuOpened);
            }
        }
    }

    private getURLPath(path: string): string {
        return path ? this.helperService.getUrlForCurrentContext(path) : path;
    }

    private getSafeURLPath(path: string): string {
        return !!path && path.startsWith('/') ? path.replace('/', '') : path;
    }

    private prepareSideNavigationItems(): void {
        this.selectedContext = this.contextManagerService.getSelectedContext();
        if (this.userService.isContextReady(this.selectedContext)) {
            const params = this.getActionAccessParameters();
            this.userService.getActionList(params).subscribe((results: ActionAccessResultDto[]) => {
                this.getTopMenuItems(results, params);
                this.getBottomMenuItems(results);
            });
        }
    }

    private getTopMenuItems(results: Array<ActionAccessResultDto>, params: ActionAccessListParameterDto): void {
        let topMenuItems: SideNavTreeInfo[] = [];
        if (params.customerId) {
            topMenuItems = this.navigationService.getPropertyNavigationSource(results);
        } else if (params.communityId) {
            topMenuItems = this.navigationService.getCommunityNavigationSource(results);
        } else {
            topMenuItems = this.navigationService.getTopNavigationSource(results);
        }

        // By default, set root level menu items to visible
        topMenuItems.forEach((r) => (r.visible = true));

        this.submenuItems.forEach((value) => {
            const menuItem = this.navigationService.getSideNavItemById(topMenuItems, value.parentId);
            if (!!menuItem) {
                menuItem.items = value.items;
            }
        });
        topMenuItems.forEach((menuItem) => {
            if (!menuItem.isWorkspace && menuItem.items.length > 1) {
                menuItem.items = this.navigationService.sortSideNavItemByName(menuItem.items);
            }
        });
        this.topMenuItems = topMenuItems;
        setTimeout(() => {
            if (!!this.sideNavMenu) {
                this.sideNavMenu.refreshTopTree();
            }
            this.setDefaultNavItemForContext();
        }, 500);
    }

    private getBottomMenuItems(results: Array<ActionAccessResultDto>): void {
        this.bottomMenuItems = this.navigationService.getBottomNavigationSource(results);
    }

    private getActionAccessParameters(): ActionAccessListParameterDto {
        const params: ActionAccessListParameterDto = new ActionAccessListParameterDto();
        if (!!this.selectedContext) {
            params.communityId = this.selectedContext.communityId || params.communityId;
            params.customerId = this.selectedContext.customerId || params.customerId;
        }
        return params;
    }

    private setDefaultNavItemForContext(): void {
        const selectedRouteItem = this.contextManagerService.getSelectedContextRouteItem();

        if (this.selectedContext && this.selectedContext.communityId) {
            this.itemToSelect = null;
            if (this.selectedContext.customerId) {
                if (selectedRouteItem) {
                    setTimeout(() => {
                        this.itemToSelect = selectedRouteItem;
                    }, 100);
                } else {
                    this.itemToSelect = '/property/:communityId/:customerId/dashboard';
                }
            } else {
                if (selectedRouteItem) {
                    setTimeout(() => {
                        this.itemToSelect = selectedRouteItem;
                    }, 100);
                } else {
                    this.itemToSelect = '/community/:communityId/dashboard';
                }
            }
        } else {
            this.itemToSelect = this.selectedRoute;
        }
    }
}
